import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, FormField, Header, Multiselect, RadioGroup, Select, SpaceBetween, StatusIndicator, Table, Tabs,
} from '@cloudscape-design/components';
import {
  PageLayout, EmptyState, PageSkeleton, useAuth, useNotify, useRoles, createApiClient,
} from '@deloitte-us-consulting/gp-sdk';
import { PersonAutosuggest, type PersonPickerEntry } from '../rbac/PersonAutosuggest';
import { useActiveAllianceOptions } from '../rbac/useActiveAlliances';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

interface RoleOption { value: string; label: string }
interface CohortOption { value: string; label: string }
interface CohortDefinition { id: string; name: string; allianceIds: string[] }

type LegStatus = 'pending' | 'approved' | 'rejected';
interface RequestLeg {
  requestId: string;
  legKey: string;
  status: LegStatus;
  allianceId?: string;
  roleId?: string;
  cohortId?: string;
  decidedBy?: string;
  decidedAt?: string;
}
interface AccessRequest {
  id: string;
  requesterId: string;
  requestedAt: string;
  kind: 'alliance' | 'role' | 'cohort' | 'all';
  allianceIds?: string[];
  roleId?: string;
  cohortId?: string;
}
interface RequestWithLegs { request: AccessRequest; legs: RequestLeg[] }
interface PendingLegView {
  requestId: string;
  requesterId: string;
  requestedAt: string;
  leg: RequestLeg;
  requesterEmail?: string | null;
  requesterName?: string | null;
}

function legLabel(leg: RequestLeg, roleOptions: RoleOption[], cohortOptions: CohortOption[]): string {
  if (leg.allianceId) return `Alliance: ${leg.allianceId}`;
  if (leg.cohortId) return `Cohort: ${cohortOptions.find((c) => c.value === leg.cohortId)?.label ?? leg.cohortId}`;
  if (leg.roleId) return `Role: ${roleOptions.find((r) => r.value === leg.roleId)?.label ?? leg.roleId}`;
  return 'All alliances';
}

/** The self-service request catalog (ADR 0012 decision 6) — every role an
 *  ordinary user may request, fetched fresh rather than hardcoded so a new
 *  admin-created team (RbacAdmin.tsx) shows up here with no deploy. */
function useRequestableRoles(api: Api): RoleOption[] {
  const [options, setOptions] = useState<RoleOption[]>([]);
  useEffect(() => {
    api
      .get<{ id: string; role_name: string }[]>('/api/rbac/role?requestable=true')
      .then((rolesList) => setOptions(rolesList.map((r) => ({ value: r.id, label: r.role_name }))))
      .catch(() => setOptions([]));
  }, [api]);
  return options;
}

/** The cohort catalog (ADR 0012 §13) — every cohort a user may request
 *  broad, whole-cohort alliance scope for. */
function useCohorts(api: Api): CohortOption[] {
  const [options, setOptions] = useState<CohortOption[]>([]);
  useEffect(() => {
    api
      .get<{ id: string; name: string }[]>('/api/rbac/cohort')
      .then((cohorts) => setOptions(cohorts.map((c) => ({ value: c.id, label: c.name }))))
      .catch(() => setOptions([]));
  }, [api]);
  return options;
}

/** Full cohort definitions (id, name, AND member allianceIds) — unlike
 *  `useCohorts` above (id/name only, for the request-form dropdown), "your
 *  access today" needs each held cohort's member alliances too, so it can
 *  skip listing them individually under "Alliance scope" (they're already
 *  covered by the "Cohort: <name>" line). */
function useCohortDefinitions(api: Api): CohortDefinition[] {
  const [cohorts, setCohorts] = useState<CohortDefinition[]>([]);
  useEffect(() => {
    api.get<CohortDefinition[]>('/api/rbac/cohort').then(setCohorts).catch(() => setCohorts([]));
  }, [api]);
  return cohorts;
}

/**
 * Self-service alliance/role access requests + their approval, for anyone
 * whose approval a request happens to route to (ADR 0010 — an alliance's
 * current managers, or a platform Admin). Deliberately routed OUTSIDE
 * RequirePermission: every authenticated user can submit a request, and
 * whether someone has anything to approve is decided by the API per-request,
 * not by a page-level permission gate.
 */
export default function AccessRequests() {
  const { accessToken } = useAuth();
  const { isPlatformAdmin, can } = useRoles();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);
  // Impersonate:Use (the `admin` role, or gp-admin) — same '*' sentinel
  // convention as the registry's Registry:Manage check (App.tsx/router.tsx).
  const canImpersonate = isPlatformAdmin || can('Impersonate:Use', { appId: '*' });
  const roleOptions = useRequestableRoles(api);
  const cohortOptions = useCohorts(api);

  return (
    <PageLayout
      title="Access Requests"
      description="Request access to an alliance's apps/data, a cohort, everything, or a team/role — and decide requests routed to you."
    >
      <Tabs
        tabs={[
          { id: 'mine', label: 'My requests', content: <MyRequestsTab api={api} roleOptions={roleOptions} cohortOptions={cohortOptions} /> },
          { id: 'approvals', label: 'Approvals', content: <ApprovalsTab api={api} roleOptions={roleOptions} cohortOptions={cohortOptions} /> },
          ...(canImpersonate ? [{ id: 'impersonate', label: 'Impersonate', content: <ImpersonateTab api={api} /> }] : []),
        ]}
      />
    </PageLayout>
  );
}

type Api = ReturnType<typeof createApiClient>;

// ── My requests ───────────────────────────────────────────────────────────────
function MyRequestsTab({ api, roleOptions, cohortOptions }: { api: Api; roleOptions: RoleOption[]; cohortOptions: CohortOption[] }) {
  const { notify } = useNotify();
  const { access } = useRoles();
  const [requests, setRequests] = useState<RequestWithLegs[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<'alliance' | 'role' | 'cohort' | 'all'>('alliance');
  const [allianceIds, setAllianceIds] = useState<{ value: string; label: string }[]>([]);
  const [role, setRole] = useState<{ value: string; label: string } | null>(null);
  const [cohort, setCohort] = useState<{ value: string; label: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const allianceCatalog = useActiveAllianceOptions(api);
  const cohortDefinitions = useCohortDefinitions(api);

  const load = async () => {
    try {
      setRequests(await api.get<RequestWithLegs[]>('/api/access-requests/mine'));
    } catch (e) {
      notify({ type: 'error', header: 'Could not load your requests', content: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);

  const submit = async () => {
    if (kind === 'alliance') {
      if (allianceIds.length === 0) {
        notify({ type: 'error', header: 'Missing input', content: 'Choose at least one alliance.' });
        return;
      }
      await doSubmit({ kind: 'alliance', allianceIds: allianceIds.map((a) => a.value) });
    } else if (kind === 'role') {
      if (!role?.value) {
        notify({ type: 'error', header: 'Missing input', content: 'Choose a role.' });
        return;
      }
      await doSubmit({ kind: 'role', roleId: role.value });
    } else if (kind === 'cohort') {
      if (!cohort?.value) {
        notify({ type: 'error', header: 'Missing input', content: 'Choose a cohort.' });
        return;
      }
      await doSubmit({ kind: 'cohort', cohortId: cohort.value });
    } else {
      await doSubmit({ kind: 'all' });
    }
  };

  const doSubmit = async (
    body:
      | { kind: 'alliance'; allianceIds: string[] }
      | { kind: 'role'; roleId: string }
      | { kind: 'cohort'; cohortId: string }
      | { kind: 'all' },
  ) => {
    setSubmitting(true);
    try {
      await api.post('/api/access-requests', body);
      notify({ type: 'success', header: 'Request submitted', content: 'Routed to the right approver automatically.' });
      setAllianceIds([]);
      setRole(null);
      setCohort(null);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Submit failed', content: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  const rows = requests.flatMap((r) => r.legs.map((leg) => ({ ...leg, requestedAt: r.request.requestedAt })));

  if (loading) return <PageSkeleton label="Loading your requests…" />;

  return (
    <SpaceBetween size="l">
      <SpaceBetween size="s">
        <Header variant="h3" description="What you currently have, before requesting more.">
          Your access today
        </Header>
        <SpaceBetween size="xs">
          <div>
            <Box variant="awsui-key-label">Roles</Box>
            {!access || access.roleIds.length === 0 ? (
              <Box color="text-body-secondary">None yet.</Box>
            ) : (
              <SpaceBetween direction="horizontal" size="xxs">
                {access.roleIds.map((id) => (
                  <StatusIndicator key={id} type="success">{access.roleNames[id] ?? id}</StatusIndicator>
                ))}
              </SpaceBetween>
            )}
          </div>
          <div>
            <Box variant="awsui-key-label">Alliance scope</Box>
            {(() => {
              const allianceAccess = access?.allianceAccess;
              if (!allianceAccess || (!allianceAccess.all && allianceAccess.allianceIds.length === 0)) {
                return <Box color="text-body-secondary">None yet.</Box>;
              }
              if (allianceAccess.all) return <StatusIndicator type="success">All alliances</StatusIndicator>;
              // Cohort membership is one line ("Cohort: Major"), not every
              // member alliance spelled out — those are still granted (this
              // is display-only; allianceIds itself is untouched), just not
              // listed twice.
              const heldCohorts = allianceAccess.cohortIds.map((id) => cohortDefinitions.find((c) => c.id === id)).filter((c) => c !== undefined);
              const coveredByCohort = new Set(heldCohorts.flatMap((c) => c.allianceIds));
              const directOnly = allianceAccess.allianceIds.filter((id) => !coveredByCohort.has(id));
              return (
                <SpaceBetween direction="horizontal" size="xxs">
                  {heldCohorts.map((c) => <StatusIndicator key={c.id} type="success">{`Cohort: ${c.name}`}</StatusIndicator>)}
                  {directOnly.map((id) => <StatusIndicator key={id} type="success">{id}</StatusIndicator>)}
                </SpaceBetween>
              );
            })()}
          </div>
        </SpaceBetween>
      </SpaceBetween>

      <Header variant="h3">Request access</Header>
      <SpaceBetween size="m">
        <FormField label="What do you need?">
          <RadioGroup
            value={kind}
            onChange={(e) => setKind(e.detail.value as 'alliance' | 'role' | 'cohort' | 'all')}
            items={[
              { value: 'alliance', label: 'Access to one or more alliances' },
              { value: 'role', label: 'A team / role' },
              { value: 'cohort', label: 'A whole cohort of alliances' },
              { value: 'all', label: 'All alliances' },
            ]}
          />
        </FormField>
        {kind === 'alliance' ? (
          <FormField
            label="Alliance(s)"
            description="From the active alliance list. Each is approved independently — you may get some before others."
          >
            <Multiselect
              selectedOptions={allianceIds}
              placeholder="Choose one or more alliances"
              options={allianceCatalog}
              onChange={(e) => setAllianceIds(e.detail.selectedOptions as { value: string; label: string }[])}
              filteringType="auto"
            />
          </FormField>
        ) : kind === 'role' ? (
          <FormField label="Role" description="Routed to a platform Admin for approval.">
            <Select
              selectedOption={role}
              placeholder="Choose a role"
              options={roleOptions}
              onChange={(e) => setRole(e.detail.selectedOption as { value: string; label: string })}
            />
          </FormField>
        ) : kind === 'cohort' ? (
          <FormField label="Cohort" description="Grants every alliance in this cohort. Routed to a platform Admin for approval.">
            <Select
              selectedOption={cohort}
              placeholder="Choose a cohort"
              options={cohortOptions}
              onChange={(e) => setCohort(e.detail.selectedOption as { value: string; label: string })}
            />
          </FormField>
        ) : (
          <Box color="text-body-secondary">Grants the universal "All alliances" bypass. Routed to a platform Admin for approval.</Box>
        )}
        <Box>
          <Button variant="primary" loading={submitting} onClick={submit}>Submit request</Button>
        </Box>
      </SpaceBetween>

      {rows.length === 0 ? (
        <EmptyState title="No requests yet" description="Requests you submit appear here with their current status." />
      ) : (
        <Table
          items={rows}
          variant="container"
          header={<Header counter={`(${rows.length})`}>Your requests</Header>}
          columnDefinitions={[
            { header: 'Requested', cell: (i) => i.requestedAt },
            { header: 'What', cell: (i) => legLabel(i, roleOptions, cohortOptions) },
            { header: 'Status', cell: (i) => i.status },
            { header: 'Decided by', cell: (i) => i.decidedBy ?? '—' },
          ]}
        />
      )}
    </SpaceBetween>
  );
}

// ── Approvals ─────────────────────────────────────────────────────────────────
function ApprovalsTab({ api, roleOptions, cohortOptions }: { api: Api; roleOptions: RoleOption[]; cohortOptions: CohortOption[] }) {
  const { notify } = useNotify();
  const [pending, setPending] = useState<PendingLegView[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = async () => {
    try {
      setPending(await api.get<PendingLegView[]>('/api/access-requests/pending'));
    } catch (e) {
      notify({ type: 'error', header: 'Could not load pending approvals', content: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);

  const decide = async (item: PendingLegView, decision: 'approve' | 'reject') => {
    const key = `${item.requestId}:${item.leg.legKey}`;
    setBusyKey(key);
    try {
      await api.post(`/api/access-requests/${item.requestId}/legs/${item.leg.legKey}/${decision}`, {});
      notify({
        type: 'success',
        header: decision === 'approve' ? 'Approved' : 'Rejected',
        content: `${legLabel(item.leg, roleOptions, cohortOptions)} for ${item.requesterName || item.requesterEmail || item.requesterId}.`,
      });
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Update failed', content: (e as Error).message });
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) return <PageSkeleton label="Loading pending approvals…" />;

  return (
    <SpaceBetween size="m">
      {pending.length === 0 ? (
        <EmptyState
          title="Nothing to approve"
          description="Requests routed to you — as an alliance's current manager, or as a platform Admin — appear here."
        />
      ) : (
        <Table
          items={pending}
          variant="container"
          header={<Header counter={`(${pending.length})`}>Pending your decision</Header>}
          columnDefinitions={[
            { header: 'Requester', cell: (i) => i.requesterName || i.requesterEmail || i.requesterId },
            { header: 'Requested', cell: (i) => i.requestedAt },
            { header: 'What', cell: (i) => legLabel(i.leg, roleOptions, cohortOptions) },
            {
              header: 'Decide',
              cell: (i) => {
                const key = `${i.requestId}:${i.leg.legKey}`;
                return (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button loading={busyKey === key} onClick={() => decide(i, 'approve')}>Approve</Button>
                    <Button loading={busyKey === key} onClick={() => decide(i, 'reject')}>Reject</Button>
                  </SpaceBetween>
                );
              },
            },
          ]}
        />
      )}
    </SpaceBetween>
  );
}

/** Every role, not just the self-service-requestable subset — a preview
 *  should be able to synthesize any real role, including AirTable-sourced
 *  titles. Safe here specifically: ImpersonateTab is only ever rendered for
 *  Impersonate:Use holders, and that permission always ships bundled with
 *  Rbac:Manage on the `admin` role (platform-roles.ts), which GET /rbac/role
 *  (no filter) already requires. */
function useAllRoleOptions(api: Api): RoleOption[] {
  const [options, setOptions] = useState<RoleOption[]>([]);
  useEffect(() => {
    api
      .get<{ id: string; role_name: string }[]>('/api/rbac/role')
      .then((rolesList) => setOptions(rolesList.map((r) => ({ value: r.id, label: r.role_name }))))
      .catch(() => setOptions([]));
  }, [api]);
  return options;
}

// ── Impersonate (admin-only) ─────────────────────────────────────────────────
function ImpersonateTab({ api }: { api: Api }) {
  const { notify } = useNotify();
  const [mode, setMode] = useState<'user' | 'synthetic'>('user');
  const [targetUserId, setTargetUserId] = useState('');
  const [roster, setRoster] = useState<PersonPickerEntry[]>([]);
  const [syntheticRoles, setSyntheticRoles] = useState<{ value: string; label: string }[]>([]);
  const [syntheticAlliances, setSyntheticAlliances] = useState<{ value: string; label: string }[]>([]);
  const [allBypass, setAllBypass] = useState(false);
  const [starting, setStarting] = useState(false);
  const allRoleOptions = useAllRoleOptions(api);
  const allianceCatalog = useActiveAllianceOptions(api);

  useEffect(() => {
    api.get<PersonPickerEntry[]>('/api/rbac/roster').then(setRoster).catch(() => setRoster([]));
  }, [api]);

  const start = async () => {
    const body =
      mode === 'user'
        ? { mode: 'user' as const, targetUserId }
        : {
            mode: 'synthetic' as const,
            roleIds: syntheticRoles.map((r) => r.value),
            allianceIds: allBypass ? [] : syntheticAlliances.map((a) => a.value),
            allBypass,
          };
    if (mode === 'user' && !targetUserId) {
      notify({ type: 'error', header: 'Missing input', content: 'Choose the user to view as.' });
      return;
    }
    setStarting(true);
    try {
      await api.post('/api/impersonation/start', body);
      notify({
        type: 'success',
        header: 'Impersonation started',
        content: 'See the banner at the top of the shell — read-only, expires in 30 minutes.',
      });
    } catch (e) {
      notify({ type: 'error', header: 'Could not start impersonation', content: (e as Error).message });
    } finally {
      setStarting(false);
    }
  };

  return (
    <SpaceBetween size="m">
      <Header variant="h3">View as…</Header>
      <Box color="text-body-secondary">
        Read-only preview. No action (approving a request, registering an app, etc.) can be taken while an
        impersonation session is active — the banner at the top of the shell lets you exit it, and only one
        session may be active at a time.
      </Box>
      <FormField label="What do you want to preview?">
        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.detail.value as 'user' | 'synthetic')}
          items={[
            { value: 'user', label: 'A specific individual (their real, actual access)' },
            { value: 'synthetic', label: 'A synthetic role/alliance combination' },
          ]}
        />
      </FormField>
      {mode === 'user' ? (
        <FormField
          label="User"
          description="Search existing people by name or email. Cannot be someone who holds gp-admin — the API rejects that."
        >
          <PersonAutosuggest value={targetUserId} onChange={setTargetUserId} people={roster} />
        </FormField>
      ) : (
        <SpaceBetween size="m">
          <FormField label="Role(s)">
            <Multiselect
              selectedOptions={syntheticRoles}
              placeholder="Choose one or more roles"
              options={allRoleOptions}
              onChange={(e) => setSyntheticRoles(e.detail.selectedOptions as { value: string; label: string }[])}
              filteringType="auto"
            />
          </FormField>
          <FormField label="All alliances">
            <Checkbox checked={allBypass} onChange={(e) => setAllBypass(e.detail.checked)}>
              Preview with the universal "All" alliance bypass
            </Checkbox>
          </FormField>
          {!allBypass && (
            <FormField label="Alliance(s)">
              <Multiselect
                selectedOptions={syntheticAlliances}
                placeholder="Choose one or more alliances"
                options={allianceCatalog}
                onChange={(e) => setSyntheticAlliances(e.detail.selectedOptions as { value: string; label: string }[])}
                filteringType="auto"
              />
            </FormField>
          )}
        </SpaceBetween>
      )}
      <Box>
        <Button variant="primary" loading={starting} onClick={start}>Start impersonation</Button>
      </Box>
    </SpaceBetween>
  );
}
